import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Confirm, useCreate, useDelete, useGetList, useNotify } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyState, RecordSection } from '../components/record/RecordPage.tsx';
import { personName } from '../relationships/relationshipApi.ts';
import { getErrorMessage } from '../utils/helperUtils.ts';
import { RenewalEditDialog } from './RenewalEditDialog.tsx';
import {
  COVERAGE_LINES,
  coverageLabel,
  formatDaysOut,
  formatPremium,
  formatRenewalDate,
  rollRenewal,
  type CoverageLine,
  type Renewal
} from './renewalApi.ts';

// One person's renewals, one deal's, or those of everyone at a company.
type RenewalFilter = { contactId: number } | { opportunityId: number } | { companyId: number };

// X-dates, one per line of coverage and date. Adding one takes three fields;
// the rest are on edit.
export const RenewalsSection = ({
  filter,
  addTo,
  description
}: {
  filter: RenewalFilter;
  // Who a renewal added here is for, and the deal working it; no form without it
  addTo?: { contactId: number; opportunityId?: number };
  description: string;
}) => {
  const notify = useNotify();
  const { data, isPending, error, refetch } = useGetList<Renewal>('renewals', {
    filter,
    sort: { field: 'renewalDate', order: 'ASC' },
    pagination: { page: 1, perPage: 100 }
  });
  const [create, { isPending: adding }] = useCreate();
  const [deleteOne] = useDelete();

  const [coverage, setCoverage] = useState<CoverageLine>('PACKAGE');
  const [renewalDate, setRenewalDate] = useState('');
  const [carrier, setCarrier] = useState('');
  const [editing, setEditing] = useState<Renewal | null>(null);
  const [removing, setRemoving] = useState<Renewal | null>(null);
  const [rollingId, setRollingId] = useState<number | null>(null);

  const showPerson = 'companyId' in filter;
  const showDeal = !('opportunityId' in filter);

  const add = () => {
    if (!addTo) return;
    create(
      'renewals',
      {
        data: {
          contactId: addTo.contactId,
          opportunityId: addTo.opportunityId ?? null,
          coverage,
          renewalDate,
          incumbentCarrier: carrier.trim() || null
        }
      },
      {
        onSuccess: () => {
          notify('Renewal added', { type: 'info' });
          setRenewalDate('');
          setCarrier('');
          refetch();
        },
        onError: (err) => notify(getErrorMessage(err), { type: 'warning' })
      }
    );
  };

  const roll = async (renewal: Renewal) => {
    setRollingId(renewal.id);
    try {
      const rolled = await rollRenewal(renewal.id);
      notify(`Now renews ${formatRenewalDate(rolled.renewalDate)}`, { type: 'info' });
      refetch();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'warning' });
    } finally {
      setRollingId(null);
    }
  };

  const remove = () => {
    if (!removing) return;
    deleteOne(
      'renewals',
      { id: removing.id, previousData: removing },
      {
        onSuccess: () => {
          notify('Renewal removed', { type: 'info' });
          refetch();
        },
        onError: (err) => notify(getErrorMessage(err), { type: 'warning' }),
        onSettled: () => setRemoving(null)
      }
    );
  };

  return (
    <RecordSection title="Renewals" description={description}>
      {error ? (
        <Alert severity="error">Could not load renewals: {getErrorMessage(error)}</Alert>
      ) : isPending ? (
        <CircularProgress size={18} />
      ) : data?.length ? (
        <Stack divider={<Divider />}>
          {data.map((renewal) => (
            <Box
              key={renewal.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatRenewalDate(renewal.renewalDate)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` · ${coverageLabel(renewal.coverage)}`}
                  </Typography>
                  {showPerson ? (
                    <>
                      {' · '}
                      <MuiLink
                        component={RouterLink}
                        to={`/contacts/${renewal.contact.id}/show`}
                        underline="hover"
                      >
                        {personName(renewal.contact)}
                      </MuiLink>
                    </>
                  ) : null}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[
                    renewal.incumbentCarrier,
                    formatPremium(renewal.estimatedPremium),
                    renewal.notes
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No carrier on file'}
                </Typography>
                {showDeal && renewal.opportunity ? (
                  <MuiLink
                    component={RouterLink}
                    to={`/opportunities/${renewal.opportunity.id}/show`}
                    underline="hover"
                    variant="body2"
                  >
                    {renewal.opportunity.dealName || 'Deal'}
                  </MuiLink>
                ) : null}
              </Box>
              {renewal.lapsed ? (
                <Chip size="small" color="warning" label="Lapsed" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {renewal.daysOut >= 0
                    ? `Renews ${formatDaysOut(renewal.daysOut)}`
                    : `Passed ${formatDaysOut(renewal.daysOut)}`}
                </Typography>
              )}
              <Stack direction="row" spacing={0.5} alignItems="center">
                {renewal.daysOut < 0 ? (
                  <Button
                    size="small"
                    disabled={rollingId === renewal.id}
                    onClick={() => roll(renewal)}
                  >
                    Roll to next year
                  </Button>
                ) : null}
                <Tooltip title="Edit renewal">
                  <IconButton
                    size="small"
                    aria-label="Edit renewal"
                    onClick={() => setEditing(renewal)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove renewal">
                  <IconButton
                    size="small"
                    aria-label="Remove renewal"
                    onClick={() => setRemoving(renewal)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <EmptyState>No renewal dates on file.</EmptyState>
      )}

      {addTo ? (
        <Box
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            if (renewalDate) add();
          }}
          sx={{
            display: 'grid',
            gap: 1.5,
            mt: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.2fr 1fr 1.2fr auto' },
            alignItems: 'center'
          }}
        >
          <TextField
            select
            size="small"
            label="Line"
            value={coverage}
            onChange={(event) => setCoverage(event.target.value as CoverageLine)}
          >
            {COVERAGE_LINES.map((line) => (
              <MenuItem key={line.value} value={line.value}>
                {line.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Renewal date"
            value={renewalDate}
            onChange={(event) => setRenewalDate(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            label="Incumbent carrier"
            value={carrier}
            onChange={(event) => setCarrier(event.target.value)}
          />
          <Button type="submit" variant="contained" disabled={!renewalDate || adding}>
            Add renewal
          </Button>
        </Box>
      ) : null}

      <RenewalEditDialog
        renewal={editing}
        onClose={() => setEditing(null)}
        onSaved={() => refetch()}
      />
      <Confirm
        isOpen={removing !== null}
        title="Remove this renewal?"
        content={
          removing
            ? [
                showPerson ? personName(removing.contact) : null,
                coverageLabel(removing.coverage),
                formatRenewalDate(removing.renewalDate)
              ]
                .filter(Boolean)
                .join(', ') + '.'
            : ''
        }
        confirm="Remove"
        onConfirm={remove}
        onClose={() => setRemoving(null)}
      />
    </RecordSection>
  );
};
